import Api from "../services/AxiosService";
import { unwrap } from "./envelope";

// svc-04 `/v1/machine-options*`. Types mirror @penn/shared-types
// MachineOption / PaginatedMachineOptions (kept local per the existing api/*.ts
// convention — see applications.ts). `price` is a decimal STRING (§J).

export interface MachineOption {
  partNo: string;
  description: string | null;
  category: string | null;
  subCategory: string | null;
  price: string | null;
  /** machine_options_availability.machine_name list (product codes). */
  availableOn: string[];
  regionId: number;
  currencyCode: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface PaginatedMachineOptions {
  data: MachineOption[];
  pagination: PaginationMeta;
}

/** One selectable machine for the "Available On" picker. */
export interface MachineNameOption {
  machineName: string;
  productName: string | null;
}

export interface ListMachineOptionsParams {
  page?: number;
  limit?: number;
  regionId?: number;
  search?: string;
}

export interface CreateMachineOptionBody {
  partNo: string;
  description?: string | null;
  category?: string | null;
  subCategory?: string | null;
  price?: string | number | null;
  availableOn: string[];
  regionId?: number;
}

/** PATCH body — partial; `partNo` is the path id (immutable). */
export interface UpdateMachineOptionBody {
  description?: string | null;
  category?: string | null;
  subCategory?: string | null;
  price?: string | number | null;
  availableOn?: string[];
  regionId?: number;
}

/** GET /v1/machine-options — paginated, region-scoped (default region 1 = EU). */
export async function listMachineOptions(
  params: ListMachineOptionsParams = {},
): Promise<PaginatedMachineOptions> {
  const res = await Api.get("/v1/machine-options", { params });
  return unwrap<PaginatedMachineOptions>(res.data);
}

/** GET /v1/machine-options/machine-names — options for the Available On picker. */
export async function listMachineNames(): Promise<MachineNameOption[]> {
  const res = await Api.get("/v1/machine-options/machine-names");
  return unwrap<MachineNameOption[]>(res.data);
}

/** GET /v1/machine-options/:partNo — for the edit page prefill. */
export async function getMachineOption(
  partNo: string,
  regionId = 1,
): Promise<MachineOption> {
  const res = await Api.get(`/v1/machine-options/${encodeURIComponent(partNo)}`, {
    params: { regionId },
  });
  return unwrap<MachineOption>(res.data);
}

/** POST /v1/machine-options. */
export async function createMachineOption(
  body: CreateMachineOptionBody,
): Promise<MachineOption> {
  const res = await Api.post("/v1/machine-options", body);
  return unwrap<MachineOption>(res.data);
}

/** PATCH /v1/machine-options/:partNo. */
export async function updateMachineOption(
  partNo: string,
  body: UpdateMachineOptionBody,
): Promise<MachineOption> {
  const res = await Api.patch(`/v1/machine-options/${encodeURIComponent(partNo)}`, body);
  return unwrap<MachineOption>(res.data);
}

/** DELETE /v1/machine-options/:partNo?regionId= — soft-delete. */
export async function deleteMachineOption(
  partNo: string,
  regionId = 1,
): Promise<{ success: true }> {
  const res = await Api.delete(`/v1/machine-options/${encodeURIComponent(partNo)}`, {
    params: { regionId },
  });
  return unwrap<{ success: true }>(res.data);
}
